import { get } from "lodash";
import { FC, Fragment, useCallback, useEffect, useState } from "react";
import { manufacturerApi } from "src/api/manufacturer";
import { setError } from "src/slices/error";
import { useDispatch } from "src/store";
import { Manufacturer } from "src/types/manufacturer";

import { Autocomplete, CircularProgress, TextField } from "@mui/material";

interface Props {
    manufacturer?: string;
    disabled?: boolean;

    setManufacturer: (value: Manufacturer) => void;
}

const ManufacturerAutocomplete: FC<Props> = ({ manufacturer, disabled, setManufacturer }) => {
    const dispatch = useDispatch();

    const [selected, setSelected] = useState<Manufacturer>();
    const [results, setResults] = useState<Manufacturer[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [search, setSearch] = useState<string>("");

    const fetchData = useCallback(async (): Promise<void> => {
        try {
            const results = await manufacturerApi.find({ search, select: "name" });

            setResults(results);
            setLoading(false);
        } catch (error) {
            dispatch(setError(error));
        }
    }, [search, dispatch]);

    const getManufacturer = useCallback(async (): Promise<void> => {
        try {
            setLoading(true);

            const result = await manufacturerApi.findById(manufacturer, { select: "name" });

            setSelected(result);
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    }, [manufacturer]);

    useEffect(() => {
        if (manufacturer) getManufacturer();
    }, [manufacturer, getManufacturer]);

    useEffect(() => {
        if (!loading) return;

        fetchData();
    }, [loading, fetchData]);

    useEffect(() => {
        if (selected) setManufacturer(selected);
        if (selected === null) setManufacturer(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    if (manufacturer && !selected)
        return (
            <Autocomplete
                key="1"
                disabled
                fullWidth
                options={[]}
                renderInput={(params): JSX.Element => (
                    <TextField fullWidth label="Hersteller" variant="outlined" {...params} />
                )}
            />
        );

    return (
        <Autocomplete
            key="2"
            disabled={disabled}
            fullWidth
            getOptionLabel={(option) => get(option, "name")}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            loading={loading}
            onChange={(x, value: Manufacturer) => {
                if (!value) setSelected(null);
                else setSelected(value);
            }}
            onClose={() => setLoading(false)}
            onOpen={() => setLoading(true)}
            options={results}
            value={selected}
            renderInput={(params): JSX.Element => (
                <TextField
                    onChange={(event) => {
                        if (event.target.value) {
                            setSearch(event.target.value);
                            setLoading(true);
                        } else {
                            setSelected(null);
                        }
                    }}
                    fullWidth
                    label="Hersteller"
                    variant="outlined"
                    {...params}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <Fragment>
                                {loading ? <CircularProgress color="primary" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </Fragment>
                        ),
                    }}
                />
            )}
        />
    );
};

export default ManufacturerAutocomplete;
